/**
 * "Gaseous state" lesson — uses an external deck.js HTML presentation,
 * different from Photosynthesis which uses the in-app LessonStage renderer.
 *
 * Mirrors the same shape as lessonData.js so the existing Audio Studio
 * and TeacherPanel components keep working. Slides correspond 1:1 with
 * the deck's 7 slides.
 */

export const gaseousLesson = {
    id: "gaseous-state",
    title: "The Gaseous State",
    subtitle: "Compressibility, diffusion and pressure",
    grade: "Grade 9",
    chapter: "Chapter 1 · Matter Around Us",
    sessionName: "The Gaseous State",
    // External deck — when present, TeacherPanel uses iframe preview and the
    // student window opens the HTML directly instead of LessonStage.
    deckUrl: "/decks/gaseous-state.html",

    sections: [
        {
            id: "concept",
            title: "Concept",
            description: "7 deck slides",
            slideIds: [
                "intro-balloon",
                "syringe",
                "cylinder-power",
                "aroma",
                "inside-gas",
                "sim-pressure",
                "mystery-solved",
            ],
        },
    ],

    slides: [
        {
            id: "intro-balloon",
            section: "concept",
            mode: "concept_teaching",
            title: "One cylinder. Hundreds of balloons. How?",
            estimatedSeconds: 30,
            tags: ["hook"],
            notes: [
                "Hook with the everyday balloon-seller image — keeps it visual and intuitive.",
            ],
            misconceptions: [],
            fragments: [
                {
                    caption:
                        "A huge volume of gas is squeezed into a tiny metal tank — high compressibility.",
                },
            ],
        },
        {
            id: "syringe",
            section: "concept",
            mode: "concept_teaching",
            title: "The Syringe Experiment",
            estimatedSeconds: 120,
            tags: ["experiment"],
            notes: [
                "Three syringes — chalk (solid), water (liquid), air (gas). Only gas compresses.",
            ],
            misconceptions: [
                "Students may think water 'compresses a little' — push the piston yourself to disprove.",
            ],
            fragments: [
                { caption: "Activity 1.11: Experimental setup." },
                { caption: "Take three 100 mL syringes and close their nozzles." },
                { caption: "Fill the first with chalk powder — the solid." },
                { caption: "Fill the second with water — the liquid." },
                { caption: "Leave the third empty — air, the gas." },
                { caption: "Look closely at the piston-and-cork mechanics." },
                {
                    caption:
                        "Observation: only the gas syringe compresses easily.",
                },
            ],
        },
        {
            id: "cylinder-power",
            section: "concept",
            mode: "concept_teaching",
            title: "Power in a Cylinder",
            estimatedSeconds: 90,
            tags: ["application"],
            notes: [
                "Connect compressibility to fuels and life-support — LPG, CNG, O₂.",
            ],
            misconceptions: [],
            fragments: [
                {
                    caption:
                        "High compressibility lets us pack large volumes of gas into small cylinders.",
                },
                { caption: "LPG — Liquefied Petroleum Gas. Home cooking fuel." },
                { caption: "CNG — Compressed Natural Gas. Clean vehicle fuel." },
                { caption: "O₂ — Oxygen cylinders. Hospital life-support." },
                { caption: "Same principle, three very different uses." },
            ],
        },
        {
            id: "aroma",
            section: "concept",
            mode: "concept_teaching",
            title: "The Aroma of Food",
            estimatedSeconds: 110,
            tags: ["diffusion"],
            notes: [
                "Use the steam-from-curry video to ground diffusion in something every student has felt.",
            ],
            misconceptions: [],
            fragments: [
                { caption: "Watch the steam — that's diffusion in motion." },
                {
                    caption:
                        "You smell hot food from meters away, but cold food only when you're close.",
                },
                { caption: "Why does temperature change everything?" },
                { caption: "1) High speed — hot particles move rapidly." },
                {
                    caption:
                        "2) Large space — gas particles have big gaps between them.",
                },
                {
                    caption:
                        "3) Result: they mix with air and diffuse extremely fast.",
                },
            ],
        },
        {
            id: "inside-gas",
            section: "concept",
            mode: "concept_teaching",
            title: "Inside the Gas State",
            estimatedSeconds: 100,
            tags: ["pressure"],
            notes: [
                "Define gas pressure carefully — Force per unit Area on the container walls.",
            ],
            misconceptions: [
                "Pressure isn't 'how much gas' — it's the force per unit area of particle collisions.",
            ],
            fragments: [
                {
                    caption:
                        "Particles are scattered, far apart, in constant random motion.",
                },
                {
                    caption:
                        "Random motion — billions of collisions with each other and the walls.",
                },
                {
                    caption:
                        "Each wall collision is a tiny push — collectively, that's gas pressure.",
                },
                { caption: "Pressure = Force ÷ Area." },
            ],
        },
        {
            id: "sim-pressure",
            section: "concept",
            mode: "concept_teaching",
            title: "Simulation: Squeeze the Gas",
            estimatedSeconds: 90,
            tags: ["interactive"],
            notes: [
                "Let students drive the simulation — Volume ↓ → Collisions ↑ → Pressure ↑.",
            ],
            misconceptions: [],
            fragments: [
                {
                    caption:
                        "Drag the piston down — watch the pressure gauge climb.",
                },
            ],
        },
        {
            id: "mystery-solved",
            section: "concept",
            mode: "concept_teaching",
            title: "Mystery Solved",
            estimatedSeconds: 100,
            tags: ["recap"],
            notes: ["Close the loop — answer the opening question crisply."],
            misconceptions: [],
            fragments: [
                {
                    caption:
                        "Mystery solved — high compressibility is why one tank fills hundreds of balloons.",
                },
                { caption: "Quick chapter recap." },
                { caption: "High compressibility — small volumes, big payload." },
                { caption: "Random high-speed motion fills the container." },
                {
                    caption:
                        "Constant wall collisions create the pressure we measure.",
                },
            ],
        },
    ],

    // MCQ / subjective / drag-drop content — surfaced through the teacher
    // panel as overlay activities on the student board (mirrors the
    // photosynthesis post-content review).
    mcqs: [
        {
            id: "gmcq-1",
            stem: "A balloon seller fills hundreds of balloons from a single small cylinder. Which property of gases makes this possible?",
            options: [
                { id: "a", text: "Low density" },
                { id: "b", text: "High compressibility", correct: true },
                { id: "c", text: "Rapid diffusion" },
                { id: "d", text: "High temperature" },
            ],
            explain:
                "Gases have huge intermolecular space, so a large volume can be squeezed into a tiny container.",
        },
        {
            id: "gmcq-2",
            stem: "The smell of hot biryani reaches the next room faster than the smell of cold dal. This is because hot particles…",
            options: [
                { id: "a", text: "Are heavier" },
                { id: "b", text: "Have less energy" },
                {
                    id: "c",
                    text: "Have more kinetic energy and diffuse faster",
                    correct: true,
                },
                { id: "d", text: "Form bonds with air molecules" },
            ],
            explain:
                "Heat raises kinetic energy. Faster-moving particles spread more rapidly through the air.",
        },
        {
            id: "gmcq-3",
            stem: "If we double the temperature of a gas in a rigid sealed container, what happens to the pressure?",
            options: [
                { id: "a", text: "Stays the same" },
                { id: "b", text: "Halves" },
                {
                    id: "c",
                    text: "Roughly doubles (particles hit harder, more often)",
                    correct: true,
                },
                { id: "d", text: "Drops to zero" },
            ],
            explain:
                "Higher temperature → faster particles → more frequent and harder collisions with the walls → higher pressure.",
        },
        {
            id: "gmcq-4",
            stem: "Why is CNG transported in heavy steel cylinders instead of normal plastic tanks?",
            options: [
                { id: "a", text: "CNG is poisonous to plastic" },
                {
                    id: "b",
                    text: "Compressed gas exerts very high pressure that plastic cannot withstand",
                    correct: true,
                },
                { id: "c", text: "Plastic floats" },
                { id: "d", text: "Steel keeps CNG cold" },
            ],
            explain:
                "Compression packs huge gas volumes into a small space → enormous pressure. Only strong steel walls can hold it safely.",
        },
        {
            id: "gmcq-5",
            stem: "Which of the following is NOT a property of gases?",
            options: [
                { id: "a", text: "Fills any container completely" },
                {
                    id: "b",
                    text: "Has a definite shape and volume",
                    correct: true,
                },
                { id: "c", text: "Highly compressible" },
                { id: "d", text: "Exerts pressure on container walls" },
            ],
            explain:
                "Gases have neither a definite shape nor a definite volume — they take both from the container.",
        },
    ],

    subjectives: [
        {
            id: "gsubj-1",
            stem: "A cooking-gas cylinder feels almost empty by weight but you can still hear gas hissing out. Explain in terms of intermolecular space and pressure.",
            expected: [
                "LPG inside is mostly a compressed liquid + gas mix.",
                "Even a small remaining amount of liquid keeps generating gas at high pressure.",
                "High compressibility means huge volumes can occupy a small mass-space.",
            ],
            commonMistake:
                "Students often assume 'empty by weight = no gas left'. The gas pressure depends on remaining liquid, not just mass.",
        },
        {
            id: "gsubj-2",
            stem: "Why does a tyre often burst on a very hot afternoon highway, even if it was inflated normally in the morning?",
            expected: [
                "Hot road heats the air inside the tyre.",
                "Heated particles move faster — kinetic energy rises.",
                "Volume is fixed (tyre wall) → pressure climbs sharply.",
                "When pressure exceeds the tyre's burst strength, it pops.",
            ],
            commonMistake:
                "It's not the rubber 'expanding' — it's the trapped air's pressure rising with temperature.",
        },
    ],

    activity: {
        id: "g-activity-drag",
        title: "Sort the property by state of matter",
        instruction:
            "Drag each property card into the state it belongs to. Some properties belong to more than one state — pick the BEST fit.",
        categories: [
            { id: "solid", label: "Solid" },
            { id: "liquid", label: "Liquid" },
            { id: "gas", label: "Gas" },
        ],
        items: [
            {
                id: "i1",
                text: "Highly compressible",
                correct: "gas",
            },
            {
                id: "i2",
                text: "Definite shape and volume",
                correct: "solid",
            },
            {
                id: "i3",
                text: "Fills any container completely",
                correct: "gas",
            },
            {
                id: "i4",
                text: "Definite volume but no fixed shape",
                correct: "liquid",
            },
            {
                id: "i5",
                text: "Particles in random high-speed motion",
                correct: "gas",
            },
            {
                id: "i6",
                text: "Particles vibrate in fixed positions",
                correct: "solid",
            },
        ],
    },

    recap: {
        title: "What we learnt today",
        bullets: [
            "Gases are highly compressible — a tiny cylinder packs a huge volume.",
            "Gas particles are in constant, random, high-speed motion.",
            "Diffusion is fast in gases — and gets faster with temperature.",
            "Pressure = Force per unit Area, from particle collisions with the walls.",
            "Real-world: LPG, CNG, oxygen cylinders, the smell of food, tyre bursts.",
        ],
    },

    realLifeExamples: [
        {
            id: "g1",
            text: "Filling a balloon at a fair.",
            headline: "Tiny cylinder, big crowd",
            description:
                "One small gas cylinder fills hundreds of balloons because high compressibility lets a huge volume of gas squeeze into a tiny tank.",
            image:
                "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "g2",
            text: "LPG cylinder in your kitchen.",
            headline: "Pressure at home",
            description:
                "LPG is compressed liquid + gas mix at room temperature. The cylinder is small because the gas is squeezed under pressure.",
            image:
                "https://images.unsplash.com/photo-1565895405138-6c3a1555da6a?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "g3",
            text: "Smelling biryani from the next room.",
            headline: "Diffusion in your nose",
            description:
                "Hot food sends fast-moving aroma particles through the air. They diffuse across the room and reach your nose in seconds.",
            image:
                "https://images.unsplash.com/photo-1565557623262-b51c2513a641?auto=format&fit=crop&w=1600&q=80",
        },
        {
            id: "g4",
            text: "Tire bursts on a hot day.",
            headline: "Heat → more pressure",
            description:
                "Heat speeds up gas particles, increasing their wall collisions. Hence the tire pressure spikes and sometimes pops.",
            image:
                "https://images.unsplash.com/photo-1601362840469-51e4d8d58785?auto=format&fit=crop&w=1600&q=80",
        },
    ],

    aiRecommendations: {
        syringe: [
            {
                id: "rec-g-sy-1",
                text: "Push the piston yourself in front of the class — kids remember the gasp.",
                action: "pause",
            },
        ],
        aroma: [
            {
                id: "rec-g-ar-1",
                text: "Ask 'Why can't we smell ice cubes from across the room?' — sparks the diffusion intuition.",
                action: "pause",
            },
        ],
    },
};

/* ---- Storyboard for the Gaseous state lesson ----
 * Matches the structure shipped in the user's brief. Audio URLs are filled
 * via the Audio Studio upload, exactly like Photosynthesis.
 */
export const gaseousStoryboard = [
    { time: 2, type: "sleep", order: 0 },
    { type: "jumpto", order: 1, slide: -1 },
    {
        slug: "",
        type: "say",
        order: 2,
        slide_no: 1,
        fragment_no: 0,
        text: "One cylinder. Hundreds of balloons. How?",
        narration:
            "Look at this — one small cylinder, yet it fills hundreds of balloons. That's because gases can be squeezed into a tiny space — a property called high compressibility.",
        audio: "",
    },
    { type: "jumpto", order: 3, slide: -2, slide_no: 2 },
    { type: "jumpto", order: 4, slide: -1 },
    {
        slug: "",
        type: "say",
        order: 5,
        slide_no: 2,
        fragment_no: 0,
        text: "Activity 1.11: Experimental Setup.",
        narration:
            "Let's verify this property with a hands-on experiment — the Syringe Experiment.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 7,
        slide_no: 2,
        fragment_no: 1,
        text: "Take three 100 mL syringes and close their nozzles.",
        narration:
            "Three identical hundred-millilitre syringes — close the nozzles tightly so nothing escapes when we push.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 9,
        slide_no: 2,
        fragment_no: 2,
        text: "Fill the first with chalk powder (solid).",
        narration: "Fill the first syringe with chalk powder — the solid sample.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 11,
        slide_no: 2,
        fragment_no: 3,
        text: "Fill the second with water (liquid).",
        narration: "Fill the second with water — that's our liquid.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 13,
        slide_no: 2,
        fragment_no: 4,
        text: "Leave the third empty (gas).",
        narration:
            "Leave the third one alone — it's full of air, our gas sample.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 15,
        slide_no: 2,
        fragment_no: 5,
        text: "",
        narration:
            "Look at the piston, the barrel, and the rubber cork sealing the nozzle.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 17,
        slide_no: 2,
        fragment_no: 6,
        text: "Observation: only the gas syringe compresses easily.",
        narration:
            "When you push the pistons, only the gas syringe compresses easily. Gases are highly compressible — solids and liquids aren't.",
        audio: "",
    },
    {
        slug: "",
        type: "concept_check",
        order: 18,
        slide_no: 2,
        fragment_no: 6,
        question:
            "If you replaced the chalk with a block of wood, would compressibility be more like water or like air?",
        narration:
            "If you replaced the chalk with a block of wood, would compressibility be more like water or like air?",
        audio: "",
    },
    { type: "jumpto", order: 19, slide: -2, slide_no: 3 },
    {
        slug: "",
        type: "say",
        order: 21,
        slide_no: 3,
        fragment_no: 0,
        text: "High compressibility allows big volumes to fit small cylinders.",
        narration:
            "High compressibility lets us pack huge amounts of gas into compact, transportable cylinders.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 23,
        slide_no: 3,
        fragment_no: 1,
        text: "LPG — Liquefied Petroleum Gas. Home cooking fuel.",
        narration: "LPG — the fuel in most home kitchens.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 25,
        slide_no: 3,
        fragment_no: 2,
        text: "CNG — Compressed Natural Gas. Clean vehicle fuel.",
        narration: "CNG powers buses and autorickshaws — a cleaner alternative.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 27,
        slide_no: 3,
        fragment_no: 3,
        text: "O₂ — oxygen cylinders. Hospital life support.",
        narration:
            "Oxygen cylinders save lives in hospitals — impossible without compressibility.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 29,
        slide_no: 3,
        fragment_no: 4,
        text: "Visual reference of LPG, CNG, and Oxygen cylinders.",
        narration:
            "Red LPG, green CNG, blue medical oxygen — all relying on high compressibility.",
        audio: "",
    },
    {
        slug: "",
        type: "concept_check",
        order: 30,
        slide_no: 3,
        fragment_no: 4,
        question:
            "Why is it more efficient to transport gases in cylinders compared to solids or liquids?",
        narration:
            "Why is it more efficient to transport gases in cylinders compared to solids or liquids?",
        audio: "",
    },
    { type: "jumpto", order: 31, slide: -2, slide_no: 4 },
    {
        slug: "",
        type: "say",
        order: 33,
        slide_no: 4,
        fragment_no: 0,
        text: "The Aroma of Food.",
        narration:
            "Look at the steam rising from this food — we encounter this every time we cook.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 35,
        slide_no: 4,
        fragment_no: 1,
        text: "Hot food smells from meters away; cold food doesn't.",
        narration:
            "Hot food smells from across the room — cold food, you have to get close.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 37,
        slide_no: 4,
        fragment_no: 2,
        text: "Why does this happen?",
        narration:
            "Why does temperature change everything about how the smell travels?",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 39,
        slide_no: 4,
        fragment_no: 3,
        text: "1. High speed — hot vapor particles move rapidly.",
        narration:
            "First — hot particles have more kinetic energy. They move fast.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 41,
        slide_no: 4,
        fragment_no: 4,
        text: "2. Large space — gas particles have big gaps.",
        narration:
            "Second — gas particles are far apart, with lots of room to move.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 43,
        slide_no: 4,
        fragment_no: 5,
        text: "3. Result: they diffuse very fast.",
        narration:
            "Result — they mix with air and diffuse very fast. That's how the aroma reaches you.",
        audio: "",
    },
    {
        slug: "",
        type: "concept_check",
        order: 44,
        slide_no: 4,
        fragment_no: 5,
        question:
            "In which season would the smell of a garbage dump spread faster — summer or winter?",
        narration:
            "In which season would the smell of a garbage dump spread faster — summer or winter?",
        audio: "",
    },
    { type: "jumpto", order: 45, slide: -2, slide_no: 5 },
    {
        slug: "",
        type: "say",
        order: 47,
        slide_no: 5,
        fragment_no: 0,
        text: "Inside the Gas State.",
        narration:
            "Gas particles aren't organized — they're scattered, far apart, in constant motion.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 49,
        slide_no: 5,
        fragment_no: 1,
        text: "Random motion — collisions everywhere.",
        narration:
            "Random motion — particles zip around at high speed, colliding with each other and the container walls.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 51,
        slide_no: 5,
        fragment_no: 2,
        text: "Gas pressure = particle hits per unit area.",
        narration:
            "Every wall collision exerts a tiny force. Sum of all those forces over the area — that's gas pressure.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 53,
        slide_no: 5,
        fragment_no: 3,
        text: "Pressure = Force ÷ Area.",
        narration: "Pressure equals Force divided by Area. Simple and powerful.",
        audio: "",
    },
    {
        slug: "",
        type: "concept_check",
        order: 54,
        slide_no: 5,
        fragment_no: 3,
        question:
            "If we heat the gas, particles hit harder. How does the pressure change?",
        narration:
            "If we heat the gas, particles hit harder. How does the pressure change?",
        audio: "",
    },
    { type: "jumpto", order: 55, slide: -2, slide_no: 6 },
    {
        slug: "",
        type: "engagement_check",
        order: 56,
        slide_no: 6,
        fragment_no: 0,
        sub_type: "activity_type_2",
        narration:
            "Compress the gas — push the piston down, watch the pressure gauge climb.",
        audio: "",
    },
    { type: "jumpto", order: 57, slide: -2, slide_no: 7 },
    {
        slug: "",
        type: "say",
        order: 59,
        slide_no: 7,
        fragment_no: 0,
        text: "Mystery solved — gas is highly compressible.",
        narration:
            "Mystery solved — one small tank fills hundreds of balloons because gas particles are highly compressible.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 61,
        slide_no: 7,
        fragment_no: 1,
        text: "Chapter recap.",
        narration: "Quick recap of everything we've covered.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 63,
        slide_no: 7,
        fragment_no: 2,
        text: "High compressibility.",
        narration:
            "High compressibility — large volumes pack into small cylinders.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 65,
        slide_no: 7,
        fragment_no: 3,
        text: "Random high speed motion.",
        narration:
            "Random high-speed motion in all directions — fills any container completely.",
        audio: "",
    },
    {
        slug: "",
        type: "say",
        order: 67,
        slide_no: 7,
        fragment_no: 4,
        text: "Pressure from continuous collisions.",
        narration:
            "Continuous wall collisions create the pressure we feel. Faster particles or more particles → higher pressure.",
        audio: "",
    },
    {
        slug: "",
        type: "concept_check",
        order: 68,
        slide_no: 7,
        fragment_no: 4,
        question:
            "If we heat a gas in a rigid closed container, why would the pressure increase?",
        narration:
            "If we heat a gas in a rigid closed container, why would the pressure increase?",
        audio: "",
    },
    {
        type: "end_class",
        order: 69,
        narration: "That wraps up the gaseous state — see you next class.",
        audio: "",
    },
];
